import { useEffect, useState } from 'react';
import { PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type KeyRole = { key_id: string; role: string | null };

type StatusPayload = {
  ok?: boolean;
  current_key_id?: string | null;
  previous_key_id?: string | null;
  roles?: KeyRole[];
  source?: string | null;
  rotation_gate?: boolean;
  mutations_disabled?: boolean;
  root_fingerprint?: string | null;
  cache_loaded?: number;
  error?: string;
  message?: string;
};

export function MasterEncryptionPage() {
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    const res = await apiFetch('/api/v1/master/system/encryption-keys');
    const body = (await res.json().catch(() => ({}))) as StatusPayload;
    if (!res.ok) {
      setError(body.message || body.error || `status ${res.status}`);
      return;
    }
    setStatus(body);
  }

  useEffect(() => {
    void load();
  }, []);

  async function postAction(
    path: string,
    body?: Record<string, string>,
    successMsg?: string,
  ) {
    setBusy(true);
    setError(null);
    setToast(null);
    try {
      const res = await apiFetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const payload = (await res.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
        new_key_id?: string;
        retired_key_id?: string;
        changed_count?: number;
      };
      if (!res.ok) throw new Error(payload.message || payload.error || `HTTP ${res.status}`);
      setToast(successMsg || 'Operazione completata');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const gateOn = Boolean(status?.rotation_gate);

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Encryption keys (DEK)"
        subtitle={
          <>
            Rotazione DEK, rewire Email/Twilio, retire previous — gate{' '}
            <code>ALLOW_DEK_ROTATION</code>
          </>
        }
      />
      <SectionCard>
        {error ? <p className="sk-error">{error}</p> : null}
        {toast ? <p className="sk-saas-banner sk-saas-banner--ok">{toast}</p> : null}

        <dl className="sk-saas-kpi" style={{ marginTop: '1rem' }}>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Current key</p>
            <p className="sk-saas-kpi__value" style={{ fontSize: '0.95rem' }}>
              {status?.current_key_id || '—'}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Previous key</p>
            <p className="sk-saas-kpi__value" style={{ fontSize: '0.95rem' }}>
              {status?.previous_key_id || '—'}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Gate</p>
            <p className="sk-saas-kpi__value">{gateOn ? 'ON' : 'OFF'}</p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Source</p>
            <p className="sk-saas-kpi__value" style={{ fontSize: '0.85rem' }}>
              {status?.source || '—'}
            </p>
          </div>
        </dl>

        <p className="sk-muted" style={{ marginTop: '0.75rem' }}>
          Root fingerprint: {status?.root_fingerprint || '—'} · cache {status?.cache_loaded ?? 0}{' '}
          · mutations {status?.mutations_disabled ? 'disabled' : 'allowed'}
        </p>

        <ol style={{ marginTop: '1.25rem', lineHeight: 1.6 }}>
          <li>Render: imposta temporaneamente <code>ALLOW_DEK_ROTATION=1</code> e ridistribuisci</li>
          <li>Rotate → Rewire Email/Twilio → verifica READY → Retire previous</li>
          <li>Riporta <code>ALLOW_DEK_ROTATION=0</code></li>
        </ol>

        <div className="sk-saas-actions" style={{ marginTop: '1.25rem', flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="sk-btn sk-btn--quiet" disabled={busy} onClick={() => void load()}>
            Aggiorna stato
          </button>
          <button
            type="button"
            className="sk-btn"
            disabled={busy || !gateOn}
            onClick={() => {
              if (!confirm('Confermi ROTATE DEK? (gate ON richiesto)')) return;
              void postAction(
                '/api/v1/master/system/encryption-keys/rotate',
                { confirm: 'ROTATE' },
                'DEK ruotato',
              );
            }}
          >
            Rotate
          </button>
          <button
            type="button"
            className="sk-btn"
            disabled={busy}
            onClick={() =>
              void postAction(
                '/api/v1/master/system/encryption-keys/rewire-master-secrets',
                {},
                'Rewire completato',
              )
            }
          >
            Rewire Email/Twilio
          </button>
          <button
            type="button"
            className="sk-btn sk-btn--quiet"
            disabled={busy || !gateOn || !status?.previous_key_id}
            onClick={() => {
              if (!confirm('Confermi RETIRE della previous key? Solo dopo rewire.')) return;
              void postAction(
                '/api/v1/master/system/encryption-keys/retire-previous',
                { confirm: 'RETIRE' },
                'Previous ritirata',
              );
            }}
          >
            Retire previous
          </button>
        </div>
      </SectionCard>
    </section>
  );
}
